import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AddOrientationSession, UpdateOrientationDay } from 'src/app/compliance/models/add-orientation-session.model';
import { ScheduleOrientationSessionType } from 'src/app/compliance/models/schedule-orientation-session-type';
import {
    CandidateAvailabilityDate,
    MultiBundleStatus,
    ScheduleOrientation,
    ScheduleOrientationPresetDate
} from 'src/app/compliance/models/schedule-orientation.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { combine } from 'src/app/shared/utilities/route';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class ScheduleOrientationService {
    private readonly coreApiPath: string;

    constructor(private readonly http: HttpClient, @Inject(APP_CONFIG) settings: Settings) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/scheduleorientation');
    }

    // ** ScheduleOrientationController.GetOrientation
    getScheduleOrientation(contractId: number): Observable<ScheduleOrientation> {
        return this.http.get<ScheduleOrientation>(`${this.coreApiPath}/${contractId}`);
    }

    // ** ScheduleOrientationController.GetOrientationByScheduleOrientationId
    getScheduleOrientationByScheduleOrientationId(orientationId: number): Observable<ScheduleOrientation> {
        return this.http.get<ScheduleOrientation>(`${this.coreApiPath}/ByScheduleOrientationId/${orientationId}`);
    }

    // ** ScheduleOrientationController.GetOrientations
    getScheduleOrientationSessions(
        contractId: number,
        args: FetchArgs
    ): Observable<PagingToken<ScheduleOrientation[]>> {
        return this.http.post<PagingToken<ScheduleOrientation[]>>(`${this.coreApiPath}/${contractId}/sessions`, args);
    }

    // ** ScheduleOrientationController.GetActionRequiredOrientationSessionsCount
    getActionRequiredOrientationSessionsCount(contractId: number): Observable<number> {
        return this.http.get<number>(`${this.coreApiPath}/${contractId}/actionRequiredOrientationSessionsCount`);
    }

    // ** ScheduleOrientationController.ScheduleOrientation
    postScheduleOrientation(contractId: number, note: string): Observable<ScheduleOrientation> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const data = `"${note}"`;
        return this.http.post<ScheduleOrientation>(`${this.coreApiPath}/${contractId}`, data, { headers });
    }

    // ** ScheduleOrientationController.ScheduleOrientationSession
    postAddNewOrientation(contractId: number, newOrientation: AddOrientationSession[]): Observable<unknown> {
        return this.http.post<unknown>(`${this.coreApiPath}/${contractId}/session`, newOrientation);
    }

    // ** ScheduleOrientationController.ScheduleOrientationDate
    postScheduleOrientationDateForCandidate(
        scheduleOrientationId: number,
        date: Date | string
    ): Observable<ScheduleOrientation> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const data = `"${date}"`;
        return this.http.post<ScheduleOrientation>(
            `${this.coreApiPath}/${scheduleOrientationId}/scheduleorientationdate`,
            data,
            { headers }
        );
    }

    // ** ScheduleOrientationController.ScheduleOrientationDate
    putScheduleOrientationDateForCandidate(dateId: number): Observable<ScheduleOrientation> {
        return this.http.put<ScheduleOrientation>(`${this.coreApiPath}/${dateId}/scheduleorientationdate`, null);
    }

    // ** ScheduleOrientationController.ScheduleOrientation
    removeScheduleOrientation(contractId: number): Observable<unknown> {
        return this.http.delete<ScheduleOrientation>(`${this.coreApiPath}/${contractId}`);
    }

    // ** ScheduleOrientationController.GetFacilityScheduleOrientationPermission
    getFacilityScheduleOrientationPermission(facilityId: number): Observable<number> {
        return this.http.get<number>(`${this.coreApiPath}/${facilityId}/permission`);
    }

    // ** ScheduleOrientationController.GetScheduleOrientationSessionTypes
    getScheduleOrientationSessionTypes(): Observable<ScheduleOrientationSessionType[]> {
        return this.http.get<ScheduleOrientationSessionType[]>(`${this.coreApiPath}/ScheduleOrientationSessionTypes`);
    }

    // ** ScheduleOrientationController.CancelScheduleOrientation
    cancelScheduleOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        return this.http.post<ScheduleOrientation>(`${this.coreApiPath}/${scheduleOrientationId}/Cancel`, null, {
            headers
        });
    }

    // ** ScheduleOrientationController.UpdateNote
    patchUpdateNote(scheduleOrientationId: number, note: string): Observable<void> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const data = `"${note}"`;
        return this.http.patch<void>(`${this.coreApiPath}/${scheduleOrientationId}/UpdateNote`, data, { headers });
    }

    // ** ScheduleOrientationController.SendOrientationToMyAya
    patchSendOrientationToMyAya(scheduleOrientationId: number): Observable<ScheduleOrientation> {
        return this.http.patch<ScheduleOrientation>(
            `${this.coreApiPath}/${scheduleOrientationId}/SendOrientationToMyAya`,
            null
        );
    }

    // ** ScheduleOrientationController.RescheduleOrientation
    postRescheduleOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation> {
        return this.http.post<ScheduleOrientation>(
            `${this.coreApiPath}/${scheduleOrientationId}/RescheduleOrientation`,
            null
        );
    }

    // ** ScheduleOrientationController.AskForNewAvailability
    postAskForNewAvailabilityOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation> {
        return this.http.post<ScheduleOrientation>(
            `${this.coreApiPath}/${scheduleOrientationId}/AskForNewAvailability`,
            null
        );
    }

    // ** ScheduleOrientationController.GetNextDayForSessionType
    getNextDayForSessionType(contractId: number, sessionTypeId: number): Observable<number> {
        return this.http.get<number>(`${this.coreApiPath}/${contractId}/${sessionTypeId}/GetNextDayForSessionType`);
    }

    // ** ScheduleOrientationController.DoesAnyOrientationExist
    doesAnyOrientationExist(contractId: number): Observable<boolean> {
        return this.http.get<boolean>(`${this.coreApiPath}/${contractId}/DoesAnyOrientationExist`);
    }

    // ** ScheduleOrientationController.GetMultiBundlesStatuses
    GetMultiBundlesStatuses(contractId: number): Observable<MultiBundleStatus[]> {
        return this.http.get<MultiBundleStatus[]>(`${this.coreApiPath}/${contractId}/GetMultiBundlesStatuses`);
    }

    // ** ScheduleOrientationController.UpdateOrientationAvailability
    putScheduleOrientationAvailability(orientation: UpdateOrientationDay): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/UpdateOrientationAvailability`;

        return this.http.put<ScheduleOrientation>(endpointUrl, orientation);
    }

    // ** ScheduleOrientationController.UpdatePresetDatesOfOrientation
    patchPresetDatesOfScheduleOrientation(
        presetDateDto: ScheduleOrientationPresetDate
    ): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/UpdatePresetDatesOfOrientation`;

        return this.http.patch<ScheduleOrientation>(endpointUrl, presetDateDto);
    }

    // ** ScheduleOrientationController.UpdateNoteOfOrientation
    patchNoteOfScheduleOrientation(scheduleOrientationId: number, note: string): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/${scheduleOrientationId}/UpdateNoteOfOrientation`;
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const data = `"${note}"`;
        return this.http.patch<ScheduleOrientation>(endpointUrl, data, { headers });
    }

    // ** ScheduleOrientationController.ScheduleMultiDayOrientations
    putScheduleOrientationMultiDayForCandidate(
        firstDayOrientationId: number,
        candidateAvailabilityDateArray: CandidateAvailabilityDate[]
    ): Observable<ScheduleOrientation> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const apiUrl = `${this.coreApiPath}/${firstDayOrientationId}/ScheduleMultiDayOrientations`;

        return this.http.put<ScheduleOrientation>(apiUrl, candidateAvailabilityDateArray, { headers });
    }

    // ** ScheduleOrientationController.MultiDayAskForNewAvailability
    putMultiDayAskForNewAvailabilityDates(
        firstDayOrientationId: number,
        orientationDays: UpdateOrientationDay[]
    ): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/${firstDayOrientationId}/MultiDayAskForNewAvailability`;
        const headers = new HttpHeaders().set('Content-Type', 'application/json');

        return this.http.put<ScheduleOrientation>(endpointUrl, orientationDays, {
            headers
        });
    }

    // ** ScheduleOrientationController.GetExistingOrientationForSessionType
    getExistingOrientationForSessionType(contractId: number, sessionTypeId: number): Observable<ScheduleOrientation[]> {
        return this.http.get<ScheduleOrientation[]>(`${this.coreApiPath}/${contractId}/${sessionTypeId}/GetExistingOrientationForSessionType`);
    }

    // ** ScheduleOrientationController.RescheduleMultiDayOrientations
    postRescheduleMultiDayOrientations(firstDayOrientationId: number, listOfOrientationIds: number[]): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/${firstDayOrientationId}/RescheduleMultiDayOrientations`;

        return this.http.post<ScheduleOrientation>(endpointUrl, listOfOrientationIds);
    }

    // ** ScheduleOrientationController.SendMultiDayOrientationToMyAya
    patchSendMultiDayOrientationToMyAya(firstDayOrientationId: number, listOfOrientationIds: number[]): Observable<ScheduleOrientation> {
        const endpointUrl = `${this.coreApiPath}/${firstDayOrientationId}/SendMultiDayOrientationToMyAya`;

        return this.http.patch<ScheduleOrientation>(endpointUrl, listOfOrientationIds);
    }
}
